import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Receipt } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { SERVICE, PAY_STATUS, PAY_METHOD, fmtDateTime } from "@/lib/util";
import { Loader, Pill, Money, payColor } from "@/components/common";
import { PageTitle, SearchBox, Table, Td, useDebounced } from "@/pages/admin/ui";
import { DateRange, Pager } from "@/pages/admin/Trips";

export default function Invoices() {
  const [sp] = useSearchParams();
  const [q, setQ] = useState("");
  const dq = useDebounced(q);
  const [f, setF] = useState({ status: sp.get("status") || "", date_from: "", date_to: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/invoices", { params: { q: dq, ...f, page } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [dq, f, page]);
  const set = (k) => (v) => { setPage(1); setF({ ...f, [k]: v }); };
  return (
    <div>
      <PageTitle title="الفواتير" sub={data ? `${data.total} فاتورة` : ""} icon={Receipt} color="text-orange-600" />
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5" data-testid="invoices-sums">
          {[["إجمالي الفواتير", data.sum_total_fils, "text-slate-900", "total"], ["المحصّل", data.sum_paid_fils, "text-green-700", "paid"], ["المتبقي", data.sum_remaining_fils, "text-red-600", "remaining"]].map(([l, v, c, k]) => (
            <div key={k} className="card p-4"><div className="text-sm text-slate-500">{l}</div><Money fils={v} className={`text-2xl ${c}`} testid={`invoices-sum-${k}`} /></div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-3 mb-4">
        <SearchBox value={q} onChange={(v) => { setPage(1); setQ(v); }} placeholder="رقم الفاتورة، رقم النقلة، الهاتف، اللوحة، السائق" testid="invoices-search" />
        <select data-testid="invoices-status-filter" className="field field-sm w-40" value={f.status} onChange={(e) => set("status")(e.target.value)}>
          <option value="">كل حالات الدفع</option>{Object.entries(PAY_STATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <DateRange from={f.date_from} to={f.date_to} setFrom={set("date_from")} setTo={set("date_to")} />
      </div>
      {!data ? <Loader /> : (
        <Table head={["رقم الفاتورة", "التاريخ", "النقلة", "الخدمة", "العميل", "السائق", "المبلغ", "المحصل", "المتبقي", "الطريقة", "الحالة"]} testid="invoices-table">
          {data.items.map((i) => <tr key={i.id} className="hover:bg-slate-50">
            <Td><Link data-testid={`invoice-row-${i.number}`} to={`/admin/invoices/${i.id}`} className="ltr text-blue-700 font-black">{i.number}</Link></Td>
            <Td className="whitespace-nowrap">{fmtDateTime(i.issued_at)}</Td><Td><span className="ltr">{i.trip_number}</span></Td><Td>{SERVICE[i.service_type]}</Td>
            <Td>{i.customer_name || "—"} <span className="ltr text-xs">{i.requester_phone}</span></Td><Td>{i.driver_name}</Td>
            <Td><Money fils={i.total_fils} /></Td><Td><Money fils={i.paid_fils} /></Td><Td><Money fils={i.total_fils - i.paid_fils} /></Td>
            <Td>{PAY_METHOD[i.payment_method] || "—"}</Td>
            <Td><Pill color={payColor(i.payment_status)}>{PAY_STATUS[i.payment_status]}</Pill>{i.corrections?.length > 0 && <Pill color="amber">مصححة</Pill>}</Td>
          </tr>)}
        </Table>
      )}
      {data && <Pager page={page} total={data.total} setPage={setPage} />}
    </div>
  );
}
